import { Box, TextField, Autocomplete } from "@mui/material";
import clsx from "clsx";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import { useSnackbar } from "react-simple-snackbar";
import { Button, Text } from "$components";
import { useStyles } from "./AddOpInRepository.styles";
import useJStyles from "$hooks/useJStyles";
import { ShippingRecord, ShippingRecordStatus } from "$types";
import { appState } from "$store";
import { ShippingRecordService } from "$services";
import { Loader } from "$components/Layout/Loader";
import {
  createDeliveryAddressFromOp,
  createShippingAddressFromOp,
} from "$utils/typeConstants";
import { snackbarErrorOptions } from "$utils/SnackbarOptions";
import { updateShippingRecord } from "$services/ShippingRecord.service";
import { checkIfShippingRecordIsCreatable } from "$utils/typeConstants/ShippingRecord";
import { CacheInvalidationAction } from "$utils/cache";

export const AddOpInRepository = ({ togglePopin, op }: any) => {
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const { filter: company } = useSelector(appState);
  const [selectedShippingRecord, selectShippingRecord] =
    useState<ShippingRecord>(undefined);

  const [openErrorSnackbar] = useSnackbar(snackbarErrorOptions);

  const getShipmentsWithPopulateOpsFilters = {
    ...(company ? { company: company._id } : {}),
    $inStatus: [ShippingRecordStatus.DRAFT, ShippingRecordStatus.TO_SHIP],
  };

  const { data: shippingRecords, isLoading } = useQuery(
    [
      CacheInvalidationAction.GET_SHIPMENTS_WITH_POPULATE_OPS,
      getShipmentsWithPopulateOpsFilters,
    ],
    () =>
      ShippingRecordService.getShipmentsWithPopulateOps(
        getShipmentsWithPopulateOpsFilters
      ),
    {
      cacheTime: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const filteredShippingRecords = useMemo(() => {
    return shippingRecords?.filter((shippingRecord: ShippingRecord) => {
      const shipmentOps = shippingRecord?.shipments.map(
        (shipment: any) => shipment.op
      );
      return !Object.prototype.hasOwnProperty.call(
        checkIfShippingRecordIsCreatable([...shipmentOps, op]),
        "error"
      );
    });
  }, [shippingRecords, op]);

  const handleSubmit = async () => {
    // if Une référence de préparation est manquante set error snackbar
    if (!op.refPreparationEntrepot) {
      openErrorSnackbar("La référence de préparation est manquante");
      return;
    }

    // if Une adresse de livraison est manquante set error snackbar
    const toAddress = createDeliveryAddressFromOp(op);
    if (!toAddress || !toAddress.name) {
      openErrorSnackbar("L'adresse de livraison est manquante");
      return;
    }

    const result = await updateShippingRecord(selectedShippingRecord.id, {
      ...selectedShippingRecord,
      shipments: [
        ...selectedShippingRecord.shipments,
        {
          opId: op._id,
          parcelId: op.parcel.id,
          fromAddress: createShippingAddressFromOp(op),
          toAddress: createDeliveryAddressFromOp(op),
          refPreparation: op.refPreparationEntrepot,
        },
      ],
    });

    if (result instanceof Error) {
      openErrorSnackbar(`Une erreur est survenue: ${result?.message}`);
      return;
    }

    selectShippingRecord(undefined);
    togglePopin();
  };

  return (
    <Box className={clsx(jclasses.jWrapper, classes.wrapper)}>
      <Text type="h2">Ajouter à un dossier</Text>
      <Box className={classes.inputContainer}>
        {isLoading ? (
          <Loader />
        ) : (
          <Autocomplete
            disableClearable
            options={filteredShippingRecords}
            noOptionsText="Aucun dossier compatible"
            getOptionLabel={(option: ShippingRecord) => option.code}
            onChange={(e, val: ShippingRecord) => selectShippingRecord(val)}
            value={selectedShippingRecord}
            renderInput={(params: any) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Sélectionner un dossier"
              />
            )}
          />
        )}
      </Box>
      <Box className={classes.buttonsContainer}>
        <Button color="secondary" onClick={togglePopin}>
          Annuler
        </Button>
        <Button
          disabled={!selectedShippingRecord || isLoading}
          onClick={handleSubmit}
        >
          Ajouter au dossier
        </Button>
      </Box>
    </Box>
  );
};
